import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  Bars3Icon,
  BellIcon,
  TrophyIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../store/userStore';
import { useAuth } from '../contexts/AuthContext';

const Layout: React.FC = () => {
  const location = useLocation();
  const { currentUser, currentSession } = useUserStore();
  const { user, logout, isLoading } = useAuth();

  const navigation = [
    { name: '홈', href: '/dashboard', icon: HomeIcon },
    { name: '좌석', href: '/dashboard/seats', icon: BuildingOfficeIcon },
    { name: '내정보', href: '/dashboard/profile', icon: UserCircleIcon },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  const isActive = (path: string) => {
    // 직접 접근 경로와 dashboard 경로 모두 고려
    if (path === '/dashboard' || path === '/dashboard/') {
      return (
        location.pathname === '/dashboard' ||
        location.pathname === '/dashboard/' ||
        location.pathname === '/dashboard/rankings' // 순위 페이지도 홈으로 간주
      );
    }
    if (path === '/dashboard/seats') {
      return (
        location.pathname === '/dashboard/seats' ||
        location.pathname === '/seats'
      );
    }
    if (path === '/dashboard/profile') {
      return (
        location.pathname === '/dashboard/profile' ||
        location.pathname === '/profile'
      );
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-secondary-50 relative">
      {/* PC 헤더 네비게이션 */}
      <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200" style={{ minHeight: '64px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ height: '64px', minHeight: '64px' }}>
            {/* 로고/타이틀 */}
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">📚 독서실</h1>
            </div>
            
            {/* 네비게이션 메뉴 */}
            <nav className="flex flex-row items-center space-x-8" style={{ whiteSpace: 'nowrap' }}>
              <Link
                to="/dashboard"
                className={`px-3 py-2 text-sm font-medium inline-block ${
                  isActive('/dashboard')
                    ? 'text-gray-900 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              >
                대시보드
              </Link>
              <Link
                to="/dashboard/seats"
                className={`px-3 py-2 text-sm font-medium inline-block ${
                  isActive('/dashboard/seats')
                    ? 'text-gray-900 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              >
                좌석선택
              </Link>
              <Link
                to="/dashboard/rankings"
                className={`px-3 py-2 text-sm font-medium inline-block ${
                  location.pathname === '/dashboard/rankings'
                    ? 'text-gray-900 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              >
                순위
              </Link>
              <Link
                to="/dashboard/profile"
                className={`px-3 py-2 text-sm font-medium inline-block ${
                  isActive('/dashboard/profile')
                    ? 'text-gray-900 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              >
                내정보
              </Link>
              <Link
                to="/dashboard/stats"
                className={`px-3 py-2 text-sm font-medium inline-block ${
                  location.pathname === '/dashboard/stats'
                    ? 'text-gray-900 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                style={{ whiteSpace: 'nowrap', display: 'inline-block' }}
              >
                통계
              </Link>
            </nav>
            
            {/* 사용자 정보 및 로그아웃 */}
            <div className="flex items-center space-x-4" style={{ whiteSpace: 'nowrap' }}>
              <div className="text-sm text-gray-700" style={{ whiteSpace: 'nowrap' }}>
                {currentUser?.name || (currentUser as any)?.username || (user as any)?.username}님
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                style={{ whiteSpace: 'nowrap' }}
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="w-full">
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* 모바일 하단 네비게이션 - K뱅크 스타일 */}
      <nav
        className="lg:hidden bg-white border-t border-secondary-100"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          boxShadow: '0 -2px 20px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div className="grid grid-cols-4 py-2">
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className="flex flex-col items-center py-3 px-2 transition-all duration-200 relative"
              >
                {/* 활성 상태 표시 */}
                {active && (
                  <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-primary-600 rounded-full"></div>
                )}

                <div
                  className={`
                  w-10 h-10 rounded-xl flex items-center justify-center mb-1 transition-all duration-200
                  ${
                    active
                      ? 'bg-primary-100 scale-110'
                      : 'hover:bg-secondary-50'
                  }
                `}
                >
                  <item.icon
                    className={`
                    h-5 w-5 transition-colors
                    ${active ? 'text-primary-600' : 'text-secondary-500'}
                  `}
                  />
                </div>

                <span
                  className={`
                  text-xs font-medium transition-colors
                  ${active ? 'text-primary-600' : 'text-secondary-500'}
                `}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}

          {/* 로그아웃 버튼 */}
          <button
            onClick={handleLogout}
            className="flex flex-col items-center py-3 px-2 transition-all duration-200 relative"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-1 transition-all duration-200 hover:bg-red-50">
              <ArrowRightOnRectangleIcon className="h-5 w-5 transition-colors text-red-500" />
            </div>

            <span className="text-xs font-medium transition-colors text-red-500">
              로그아웃
            </span>
          </button>
        </div>
      </nav>

      {/* 모바일 하단 여백 (네비게이션 바 높이만큼) */}
      <div className="h-20 lg:hidden"></div>
    </div>
  );
};

export default Layout;
